import React, { useCallback, useState } from 'react';

import { Dialog } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useSnackbar } from 'notistack';

import AddDepartmentForm from './AddDepartmentForm';
import ValidationException from '../../exceptions/ValidationException';
import FacilityService from '../../services/facility.service';

const useStyles = makeStyles(() => ({
  dialog: {
    '& .MuiDialog-paper': {
      borderRadius: '8px',
      margin: '16px',
      maxWidth: '500px',
      overflow: 'visible',
      width: '100%',
    },
  },
  modalContent: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    padding: '36px 24px',
  },
}));

const AddDepartmentModal = ({ open, onClose, onAdd, facilityName, facilityId, initialData = null, isEditing = false }) => {
  const classes = useStyles();
  const [validationErrors, setValidationErrors] = useState(null);
  const [resetData, setResetData] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const facilityService = new FacilityService();

  const defaultData = React.useMemo(() => {
    return { ...initialData, facilityId };
  }, [facilityId, initialData]);

  const addDepartment = async (departmentData) => {
    try {
      setIsSubmitting(true);

      // Ensure facilityId is present
      if (!departmentData.facilityId) {
        throw new Error('Facility ID is required to create a department');
      }

      const result = isEditing ? await facilityService.updateDepartment(initialData.id, facilityId, departmentData) : await facilityService.createDepartment(departmentData);

      if (onAdd) {
        onAdd(result);
      }

      enqueueSnackbar(`Department ${isEditing ? 'updated' : 'added'} successfully`, { variant: 'success' });

      return result;
    } catch (error) {
      if (error.response?.status === 422) {
        throw new ValidationException(error.response.data.errors);
      }
      const errorMessage = error.response?.data?.message || error.message || `Failed to ${isEditing ? 'update' : 'add'} department`;
      enqueueSnackbar(errorMessage, { variant: 'error' });
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const onAddDepartment = async (formData) => {
    try {
      setResetData(false);

      await addDepartment(formData);

      setValidationErrors(null);
      setResetData(true);
      onClose();
    } catch (exception) {
      if (exception instanceof ValidationException) {
        setValidationErrors(exception.errors);
      }
    }
  };

  const handleSetExternalError = useCallback((fieldName, value) => {
    setValidationErrors((errors) => ({ ...(errors || {}), [fieldName]: value }));
  }, []);

  return (
    <Dialog open={open} onClose={onClose} className={classes.dialog}>
      <div className={classes.modalContent}>
        <AddDepartmentForm
          resetData={resetData}
          onAddDepartment={onAddDepartment}
          validationErrors={validationErrors}
          defaultData={defaultData}
          isSubmitting={isSubmitting}
          onSetExternalError={handleSetExternalError}
          onCancel={onClose}
          title={isEditing ? 'Edit Department' : 'Add New Department'}
          subtitle={isEditing ? 'Edit the department information' : 'Add a new department to facility'}
          facilityName={facilityName}
          isEditing={isEditing}
        />
      </div>
    </Dialog>
  );
};

export default AddDepartmentModal;
